import React, { useState } from "react";
import clsx from "clsx";
import format from "date-fns/format";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { getPersonFullName } from "utils/person";
import GreenCheckbox from "components/GreenCheckbox";
import Loader from "components/Loader";
import Textarea from "components/Textarea";
import { useRacehorse360Api, useSetQueryData } from "hooks/api";
import {
  withClickBlocker,
  WithClickBlockerProps
} from "components/BlockableClickContext";
import { WORKOUT_REQUEST_TOTAL_RESULTS_QUERY_KEY } from "../../../helper";

import useStyles from "./styles";

interface IProps {
  workoutRequestId: string;
  horse: racehorse360.IHorse;
  isRowRejected: boolean;
  setIsRowRejected: (boolean) => void;
  disabled: boolean;
  uncheckExamPass: (string) => void;
}

const RejectRequestDialog: React.FC<IProps & WithClickBlockerProps> = props => {
  const {
    workoutRequestId,
    horse,
    isRowRejected,
    setIsRowRejected,
    disabled,
    uncheckExamPass,
    handleBlockableClick
  } = props;

  const { filterWorkoutRequests } = useSetQueryData();

  const [isOpen, setIsOpen] = useState(false);
  const [note, setNote] = useState("");
  const [isDiagnosticsRequired, setIsDiagnosticsRequired] = useState(false);

  const classes = useStyles();

  const { useRejectWorkoutRequest } = useRacehorse360Api();

  const { mutateAsync: rejectWorkoutRequest, isPending } =
    useRejectWorkoutRequest();

  const handleOpen = () => {
    handleBlockableClick(() => {
      uncheckExamPass(workoutRequestId);
      setIsRowRejected(true);
      setIsOpen(true);
    });
  };

  const handleClose = () => {
    setNote("");
    setIsRowRejected(false);
    setIsOpen(false);
  };

  const handleNoteChange = value => {
    setNote(value);
  };

  const handleCheckboxChange = event => {
    setIsDiagnosticsRequired(event.target.checked);
  };

  const handleSave = () => {
    rejectWorkoutRequest({
      id: workoutRequestId,
      comment: note,
      isImagingDiagnosticsRequired: isDiagnosticsRequired
    }).then(workoutRequest => {
      handleClose();
      filterWorkoutRequests(
        [workoutRequest.id],
        WORKOUT_REQUEST_TOTAL_RESULTS_QUERY_KEY
      );
    });
  };

  return (
    <>
      <IconButton
        size={"small"}
        className={clsx(classes.menuButton, classes.rejectedButton, {
          [classes.rejectedButton__active]: isRowRejected
        })}
        onClick={handleOpen}
        disabled={disabled}
      >
        {isRowRejected && <CloseIcon className={classes.rejectedIcon} />}
      </IconButton>
      {isOpen && (
        <Dialog
          classes={{
            paper: classes.dialogPaper
          }}
          open={isOpen}
        >
          {isPending && <Loader overlay />}
          <DialogTitle className={classes.dialogTitle}>
            Rejection Note
            <IconButton
              color="primary"
              onClick={handleClose}
              size={"small"}
              classes={{
                root: classes.dialogCloseButton
              }}
            >
              <CloseIcon className={classes.dialogCloseButtonIcon} />
            </IconButton>
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <span className={classes.horseName}>
              {horse.name.toUpperCase()}
            </span>
            <span className={classes.trainerName}>
              {getPersonFullName(horse.trainer)}
            </span>
            <Textarea
              className={classes.textarea}
              maxLength={90}
              label={format(new Date(), "MM/dd-yyyy - hh:mm aa")}
              placeholder={"Add Note"}
              rows={7}
              value={note}
              defaultValue={note}
              onChange={handleNoteChange}
            />
            <FormGroup row>
              <FormControlLabel
                control={
                  <GreenCheckbox
                    value={isDiagnosticsRequired}
                    onChange={handleCheckboxChange}
                    name="diagnosticsCheckbox"
                  />
                }
                label="Requires Imaging / Diagnostics"
                className={classes.diagnosticsLabel}
              />
            </FormGroup>
          </DialogContent>
          <DialogActions className={clsx(classes.dialogActions)}>
            <div>
              <Button className={classes.buttonCancel} onClick={handleClose}>
                CANCEL
              </Button>
              <Button
                className={classes.buttonSave}
                onClick={handleSave}
                disabled={!note.trim().length}
              >
                SAVE
              </Button>
            </div>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default withClickBlocker<IProps>(RejectRequestDialog);
